// shared/src/schemas/brief.ts
// Zod schema for `briefs` table — V2 narrow shape per migration 0018.
//
// Source of truth: migrations/0018_wipe_and_reshape.sql:211-224
// (the V2 reshape per Phase 6 §K.3 — docs/2026-05-25-flex-schema-brief-
// revision.md §K.1).
//
// V2 reshape (2026-05-25):
//   · DROPPED `metadata` TEXT col — moved to details.metadata JSON path.
//   · DROPPED 4 anchoring metric cols (bias_cosine_sim, max_ticker_drop_pct,
//     conf_high_drop_pct, blank_rate_mult) — moved to `brief_anchoring`
//     sidecar table (one row per brief; computed at f.1.finalize).
//   · ADDED `schema_version INTEGER NOT NULL DEFAULT 2`.
//   · ADDED `details TEXT NOT NULL DEFAULT '{}'` JSON sidecar.
//
// A2 drift-detector enforces field-name parity with the V2 DDL.
//
// 14 columns total: 10 keys/status + run_shadow_cost_usd (0081) + schema_version +
// details + (joinable brief_anchoring sidecar — that's a separate Zod schema if needed).

import { z } from 'zod';

export const BriefStatusEnum = z.enum(['running', 'done', 'failed', 'cost_exceeded', 'aborted']);
export const BriefRegimeEnum = z.enum(['risk-on', 'risk-off', 'mixed', 'transitioning']);

export const BriefSchema = z.object({
  id:                   z.string(),                          // UUIDv7
  created_at:           z.string().datetime(),
  finalized_at:         z.string().datetime().nullable(),
  chain_run_id:         z.string(),                          // Cloudflare Workflow instance ID
  status:               BriefStatusEnum,
  last_stage:           z.string().nullable(),
  error_message:        z.string().max(250).nullable(),
  run_cost_usd:         z.number().nonnegative().nullable(),
  // 0081 (2026-07-09) — LIVE run cost excludes the Pro-shadow A/B challenger; the
  // shadow's cost is tracked separately here so /operations can show it without
  // inflating run_cost_usd. Nullable (pre-0081 briefs + briefs with no shadow).
  run_shadow_cost_usd:  z.number().nonnegative().nullable(),
  tickers:              z.string(),                          // JSON array as TEXT; parse downstream
  regime:               BriefRegimeEnum.nullable(),
  schema_version:       z.number().int(),
  // LLM-display payload (JSON-as-TEXT). The legacy `metadata` column was
  // folded into `details.metadata` per §K.1 — readers that previously did
  // `json_extract(metadata, '$.origin')` now read
  // `json_extract(details, '$.metadata.origin')`.
  details:              z.string(),
});

export type Brief = z.infer<typeof BriefSchema>;
